import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;
const FALLBACK_COLOR = '#6366f1';
const COLOR_PRESETS: string[] = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#d946ef',
  '#ec4899',
  '#64748b',
];

interface HsvColor {
  hue: number;
  saturation: number;
  value: number;
}

interface ColorPickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  presets?: string[];
  fallbackColor?: string;
  placeholder?: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function toHexPart(value: number): string {
  return Math.round(value).toString(16).padStart(2, '0');
}

function hsvToHex({ hue, saturation, value }: HsvColor): string {
  const normalizedSaturation = saturation / 100;
  const normalizedValue = value / 100;
  const chroma = normalizedValue * normalizedSaturation;
  const hueSection = hue / 60;
  const x = chroma * (1 - Math.abs((hueSection % 2) - 1));
  const match = normalizedValue - chroma;

  let red = 0;
  let green = 0;
  let blue = 0;

  if (hueSection >= 0 && hueSection < 1) {
    red = chroma;
    green = x;
  } else if (hueSection >= 1 && hueSection < 2) {
    red = x;
    green = chroma;
  } else if (hueSection >= 2 && hueSection < 3) {
    green = chroma;
    blue = x;
  } else if (hueSection >= 3 && hueSection < 4) {
    green = x;
    blue = chroma;
  } else if (hueSection >= 4 && hueSection < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  return `#${toHexPart((red + match) * 255)}${toHexPart((green + match) * 255)}${toHexPart((blue + match) * 255)}`;
}

function hexToHsv(hex: string): HsvColor {
  const safeHex = HEX_COLOR_REGEX.test(hex) ? hex : FALLBACK_COLOR;
  const red = parseInt(safeHex.slice(1, 3), 16) / 255;
  const green = parseInt(safeHex.slice(3, 5), 16) / 255;
  const blue = parseInt(safeHex.slice(5, 7), 16) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;

  let hue = 0;
  if (delta !== 0) {
    if (max === red) {
      hue = 60 * (((green - blue) / delta) % 6);
    } else if (max === green) {
      hue = 60 * ((blue - red) / delta + 2);
    } else {
      hue = 60 * ((red - green) / delta + 4);
    }
  }

  return {
    hue: Math.round(hue < 0 ? hue + 360 : hue),
    saturation: max === 0 ? 0 : Math.round((delta / max) * 100),
    value: Math.round(max * 100),
  };
}

function normalizeHex(value: string): string {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return trimmedValue;
  }
  return trimmedValue.startsWith('#') ? trimmedValue : `#${trimmedValue}`;
}

export function ColorPicker({
  id,
  value,
  onChange,
  disabled = false,
  invalid = false,
  className,
  presets = COLOR_PRESETS,
  fallbackColor = FALLBACK_COLOR,
  placeholder,
}: ColorPickerProps) {
  const safeFallbackColor = HEX_COLOR_REGEX.test(fallbackColor) ? fallbackColor : FALLBACK_COLOR;
  const colorValue = HEX_COLOR_REGEX.test(value) ? value : safeFallbackColor;
  const hsvColor = hexToHsv(colorValue);
  const displayColor = HEX_COLOR_REGEX.test(value) ? value : safeFallbackColor;
  const displayText = value || placeholder || safeFallbackColor;
  const hueColor = `hsl(${hsvColor.hue} 100% 50%)`;

  const updateSaturationValue = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
    onChange(hsvToHex({
      hue: hsvColor.hue,
      saturation: Math.round(x * 100),
      value: Math.round((1 - y) * 100),
    }));
  };

  const handleSaturationPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    updateSaturationValue(event);
  };

  const handleSaturationPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.buttons !== 1) {
      return;
    }
    updateSaturationValue(event);
  };

  const handleHueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(hsvToHex({
      ...hsvColor,
      hue: Number(event.target.value),
    }));
  };

  const handleHexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(normalizeHex(event.target.value));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-invalid={invalid}
          className={cn('w-full justify-start gap-3 font-normal', className)}
        >
          <span
            className="size-5 shrink-0 rounded-full border border-input"
            style={{ backgroundColor: displayColor }}
          />
          <span className={cn('font-mono', !value && 'text-muted-foreground')}>{displayText}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="flex flex-col gap-3">
          <div
            role="slider"
            aria-label="Насыщенность и яркость цвета"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={hsvColor.value}
            aria-valuetext={displayColor}
            tabIndex={disabled ? -1 : 0}
            className="relative h-36 cursor-crosshair rounded-md border border-input outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            style={{
              background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), ${hueColor}`,
            }}
            onPointerDown={handleSaturationPointerDown}
            onPointerMove={handleSaturationPointerMove}
          >
            <span
              className="absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
              style={{
                left: `${hsvColor.saturation}%`,
                top: `${100 - hsvColor.value}%`,
                backgroundColor: displayColor,
              }}
            />
          </div>

          <input
            aria-label="Оттенок цвета"
            type="range"
            min={0}
            max={359}
            value={hsvColor.hue}
            onChange={handleHueChange}
            disabled={disabled}
            className="h-3 w-full cursor-pointer appearance-none rounded-full border border-input bg-transparent disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              accentColor: hueColor,
              background: 'linear-gradient(to right, #ef4444, #f59e0b, #22c55e, #06b6d4, #3b82f6, #8b5cf6, #ef4444)',
            }}
          />

          <div className="grid grid-cols-6 gap-2">
            {presets.map((presetColor) => (
              <button
                key={presetColor}
                type="button"
                aria-label={`Выбрать цвет ${presetColor}`}
                disabled={disabled}
                className={cn(
                  'size-8 rounded-md border border-input transition-[transform,box-shadow] hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                  presetColor.toLowerCase() === displayColor.toLowerCase() && 'ring-2 ring-ring ring-offset-2',
                )}
                style={{ backgroundColor: presetColor }}
                onClick={() => onChange(presetColor)}
              />
            ))}
          </div>

          <Input
            aria-label="Hex-цвет"
            value={value}
            onChange={handleHexChange}
            disabled={disabled}
            aria-invalid={invalid}
            className="font-mono"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
